/**
 * React context for preferences questionnaire — fetches API, maps to questionnaireMap, exposes session.
 *
 * Belongs here: query, context provider, usePreferencesContext, submit wiring.
 * Does not belong here: page handlers, pure flow rules, or question UI components.
 */
import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'
import type {
  FundPreferenceType,
  PreferencesAnswersMap,
  PreferencesQuestionApi,
  PreferenceQuestionnaireDataResponse
} from '../setup/types'
import type { PreferencesQuestionnaireMap } from '../../question-utils/preferencesMappers'
import type { ImageAvailability } from '../../shared/types/common.types'
import type { SlideDirection } from '../../shared/transitions/DirectionalSlideSwap'
import type { ExpertReviewCameFrom } from '../../navigation/expertReviewNavigation'
import { usePreferencesQuestionnaireQuery } from '../../api/usePreferencesQuestionnaireQuery'
import { useSubmitPreferencesMutation } from '../../api/useSubmitPreferencesMutation'
import { buildQuestionnaireMap, mapPreferenceApiItemToFundPreferenceType } from '../../question-utils/preferencesMappers'
import type { PreferencesLeave, PreferencesAnswerResult } from './usePreferencesQuestionnaire'
import { usePreferencesQuestionnaire } from './usePreferencesQuestionnaire'
import { usePreferencesImages } from '../usePreferencesImages'
import { useExpertReviewStatusContext } from '../../providers/ExpertReviewStatusContext'

export interface PreferencesContextType {
  questionnaireData: PreferenceQuestionnaireDataResponse | undefined
  questionnaireMap: PreferencesQuestionnaireMap
  preferenceTypes: FundPreferenceType[]
  isPreferenceTypesLoading: boolean
  isPreferenceTypesError: boolean
  refetchPreferenceTypes: () => void
  getPreferenceTypeById: (preferenceTypeId: string) => FundPreferenceType | null
  getVisibleQuestionsForPreferenceType: (
    preferenceTypeId: string,
    answersOverride?: PreferencesAnswersMap
  ) => PreferencesQuestionApi[]
  getCurrentQuestionForPreferenceType: (
    preferenceTypeId: string,
    questionIndex: number
  ) => PreferencesQuestionApi | null
  getQuestionAtIndices: (
    preferenceTypeIndex: number,
    questionIndex: number
  ) => PreferencesQuestionApi | null
  preferenceTypeIndex: number
  questionIndex: number
  currentPreferenceType: FundPreferenceType | null
  currentQuestion: PreferencesQuestionApi | null
  visibleQuestionsForCurrentType: PreferencesQuestionApi[]
  slideDirection: SlideDirection
  goToQuestion: (
    nextPreferenceTypeIndex: number,
    nextQuestionIndex: number,
    direction: SlideDirection
  ) => void
  draftAnswers: PreferencesAnswersMap
  committedAnswers: PreferencesAnswersMap
  committedAnswersVersion: number
  lastSubmittedAnswersVersion: number
  isAnswersVersionOutOfSync: boolean
  /** POST then /status refresh — review autosubmit / Save when versions are out of sync. */
  submitPreferencesAndStatusRefresh: (
    vars: {
      questionnaireId: string
      answers: PreferencesAnswersMap
      answersVersion: number
    },
    options?: { silent?: boolean }
  ) => Promise<void>
  /** Hub: after full review once (or seeded done), open review instead of list. */
  hasReachedReviewOnce: boolean
  markReachedReview: () => void
  /** Real answer tap — on allDone call leaveQuestionnaire(leave). */
  answerQuestion: (
    questionId: string,
    value: string | number | string[],
    options?: { isSingleTypeEdit?: boolean }
  ) => PreferencesAnswerResult
  /** Selection / multi-select only — writes draftAnswers, does not move forward. */
  updateDraftAnswer: (questionId: string, value: string | number | string[]) => void
  clearAnswer: (questionId: string) => void
  /** Commit draft without a new answer; then call leaveQuestionnaire(leave). */
  finishFromDraftAnswers: () => PreferencesLeave
  leaveQuestionnaire: (leave: PreferencesLeave) => void
  /** Discard draft and leave answering (pop questionnaire route). */
  cancelAnswering: () => void
  /** Discard draft and replace to preferences review (invalid answering route). */
  returnToReview: () => void
  /** Clear one preference type into draft and open edit route. */
  beginEditOne: (preferenceTypeId: string) => void
  /** Discard draft only and open full questionnaire (committed stays until AllDone / CommitDraft). */
  beginEditAll: (options?: { cameFrom?: ExpertReviewCameFrom }) => void
  /** Discard uncommitted draft only (e.g. hub / menu leave). */
  clearSession: () => void
  preferenceTypeIconAvailabilityById: Record<string, ImageAvailability>
}

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined)

interface PreferencesProviderProps {
  children: ReactNode
  preferenceTypeIdFromPathParam?: string
}

/** Fetches questionnaire data, builds maps, and wraps children with session state. */
export function PreferencesProvider({
  children,
  preferenceTypeIdFromPathParam
}: PreferencesProviderProps) {
  const {
    data: preferencesQuestionnaireData,
    isLoading: isPreferencesQuestionnaireLoading,
    isError: isPreferencesQuestionnaireError,
    refetch: refetchPreferencesQuestionnaire
  } = usePreferencesQuestionnaireQuery()

  const { statusData, isStatusLoading, isStatusError } = useExpertReviewStatusContext()

  // Pages that need answers treat status settle like preference-types loading
  const isPreferenceTypesLoading = isPreferencesQuestionnaireLoading || isStatusLoading

  const preferenceList = useMemo(
    () => preferencesQuestionnaireData?.preferenceList ?? [],
    [preferencesQuestionnaireData]
  )

  // Normalize API payload into the shape buildQuestionnaireMap expects
  const questionnaireData = useMemo(() => {
    if (!preferencesQuestionnaireData) return undefined
    return {
      questionnaireId: preferencesQuestionnaireData.questionnaireId,
      status: preferencesQuestionnaireData.status,
      questionGroups: preferenceList.map((preference) => ({
        groupId: preference.id,
        isNewPreference: preference.isNewPreference === true ? true : undefined,
        questions: preference.questions,
        reviewResolver: Array.isArray(preference.reviewResolver)
          ? preference.reviewResolver
          : undefined
      }))
    }
  }, [preferencesQuestionnaireData, preferenceList])

  // UI-facing preference types (icons, labels) — questionIds filled in below once map exists
  const preferenceTypesFromList = useMemo((): FundPreferenceType[] => {
    return preferenceList.map(mapPreferenceApiItemToFundPreferenceType)
  }, [preferenceList])

  const { preferenceTypeIconAvailabilityById } = usePreferencesImages(preferenceTypesFromList)

  const questionnaireMap = useMemo((): PreferencesQuestionnaireMap => {
    if (!questionnaireData) return {}
    return buildQuestionnaireMap(questionnaireData)
  }, [questionnaireData])

  // Merge ordered questionIds from map onto each preference type for cursor/back rules
  const preferenceTypes = useMemo((): FundPreferenceType[] => {
    if (preferenceTypesFromList.length === 0) return []
    if (Object.keys(questionnaireMap).length === 0) return preferenceTypesFromList
    return preferenceTypesFromList.map((preferenceType) => ({
      ...preferenceType,
      questionIds: questionnaireMap[preferenceType.id]?.questionIds ?? []
    }))
  }, [preferenceTypesFromList, questionnaireMap])

  const flow = usePreferencesQuestionnaire({
    preferenceTypeIdFromPathParam,
    preferenceTypes,
    questionnaireMap,
    rawQuestionnaireData: questionnaireData,
    expertReviewStatus: {
      isStatusLoading,
      isStatusError,
      questionnaireStatus: statusData?.preferenceQuestionnaire?.status
    }
  })

  const { submitPreferencesAndStatusRefresh } = useSubmitPreferencesMutation({
    onSubmittedAnswersVersion: flow.markSubmittedAnswersVersion
  })

  const refetchPreferenceTypes = useCallback(() => {
    refetchPreferencesQuestionnaire()
  }, [refetchPreferencesQuestionnaire])

  /** Lookup by id for review cards and single-type edit entry. */
  const getPreferenceTypeById = useCallback(
    (preferenceTypeId: string): FundPreferenceType | null =>
      preferenceTypes.find((preferenceType) => preferenceType.id === preferenceTypeId) ?? null,
    [preferenceTypes]
  )

  /** Visible question at index within one preference type (respects branch answers). */
  const getCurrentQuestionForPreferenceType = useCallback(
    (preferenceTypeId: string, questionIndexForType: number): PreferencesQuestionApi | null => {
      const visible = flow.getVisibleQuestionsForPreferenceType(preferenceTypeId)
      return visible[questionIndexForType] ?? null
    },
    [flow.getVisibleQuestionsForPreferenceType]
  )

  /** Resolve question from session cursor indices — used by progress and back targets. */
  const getQuestionAtIndices = useCallback(
    (preferenceTypeIndexForQuestion: number, questionIndexForType: number): PreferencesQuestionApi | null =>
      getCurrentQuestionForPreferenceType(
        preferenceTypes[preferenceTypeIndexForQuestion]?.id ?? '',
        questionIndexForType
      ) ?? null,
    [preferenceTypes, getCurrentQuestionForPreferenceType]
  )

  const value = useMemo(
    () => ({
      questionnaireData,
      questionnaireMap,
      preferenceTypes,
      isPreferenceTypesLoading,
      isPreferenceTypesError: isPreferencesQuestionnaireError,
      refetchPreferenceTypes,
      getPreferenceTypeById,
      getVisibleQuestionsForPreferenceType: flow.getVisibleQuestionsForPreferenceType,
      getCurrentQuestionForPreferenceType,
      getQuestionAtIndices,
      preferenceTypeIndex: flow.preferenceTypeIndex,
      questionIndex: flow.questionIndex,
      currentPreferenceType: flow.currentPreferenceType,
      currentQuestion: flow.currentQuestion,
      visibleQuestionsForCurrentType: flow.visibleQuestionsForCurrentType,
      slideDirection: flow.slideDirection,
      goToQuestion: flow.goToQuestion,
      draftAnswers: flow.draftAnswers,
      committedAnswers: flow.committedAnswers,
      committedAnswersVersion: flow.committedAnswersVersion,
      lastSubmittedAnswersVersion: flow.lastSubmittedAnswersVersion,
      isAnswersVersionOutOfSync: flow.isAnswersVersionOutOfSync,
      submitPreferencesAndStatusRefresh,
      hasReachedReviewOnce: flow.hasReachedReviewOnce,
      markReachedReview: flow.markReachedReview,
      answerQuestion: flow.answerQuestion,
      updateDraftAnswer: flow.updateDraftAnswer,
      clearAnswer: flow.clearAnswer,
      finishFromDraftAnswers: flow.finishFromDraftAnswers,
      leaveQuestionnaire: flow.leaveQuestionnaire,
      cancelAnswering: flow.cancelAnswering,
      returnToReview: flow.returnToReview,
      beginEditOne: flow.beginEditOne,
      beginEditAll: flow.beginEditAll,
      clearSession: flow.clearSession,
      preferenceTypeIconAvailabilityById
    }),
    [
      questionnaireData,
      questionnaireMap,
      preferenceTypes,
      isPreferenceTypesLoading,
      isPreferencesQuestionnaireError,
      refetchPreferenceTypes,
      getPreferenceTypeById,
      flow,
      getCurrentQuestionForPreferenceType,
      getQuestionAtIndices,
      submitPreferencesAndStatusRefresh,
      preferenceTypeIconAvailabilityById
    ]
  )

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>
}

/** Read preferences session + API data — must render under PreferencesProvider. */
export function usePreferencesContext() {
  const context = useContext(PreferencesContext)
  if (!context) {
    throw new Error('usePreferencesContext must be used within a PreferencesProvider')
  }
  return context
}
